import { saveAs } from 'file-saver';
import { useState } from 'react';
import { toast } from 'react-toastify';

import DownloadIcon from '../../../assets/svg/DownloadIcon';
import { createTranscriptDocument } from '../../../helpers';
import { BasicModal, Button } from '../../Shared';

export const ReadCallNotesModal = ({ open, onClose, recording }) => {
  const [isTranscriptLoading, setIsTranscriptLoading] = useState(false);

  const downloadTranscript = async () => {
    setIsTranscriptLoading(true);

    try {
      const blob = await createTranscriptDocument(recording);
      saveAs(blob, 'Transcript.docx');
      setIsTranscriptLoading(false);
    } catch {
      setIsTranscriptLoading(false);
      toast.error('Something went wrong, please try again!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const clTranscriptParsed = JSON.parse(recording.clTranscript);

  return (
    <BasicModal open={open} onClose={isTranscriptLoading ? undefined : onClose}>
      <div className="bg-white w-[536px] rounded-2xl shadow-[-40px_40px_80px_-8px_rgba(0,0,0,0.24)]">
        <div className="flex items-center justify-between py-4 pl-5 pr-4 border-b border-b-base-200">
          <span className="text-base-1000 font-semibold text-[22px]/[28px]">
            Transcript
          </span>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-transparent text-sm font-semibold text-gray-800 transition hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-50 dark:text-white dark:hover:bg-gray-700 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
            data-hs-overlay="#edit-contact-modal"
            onClick={onClose}
            disabled={isTranscriptLoading}
          >
            <span className="sr-only">Close</span>
            <svg
              className="h-4 w-4 flex-shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
        <div className="flex flex-col gap-4 py-4 px-5">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <svg
                width="15"
                height="19"
                viewBox="0 0 15 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.8006 5.32913L10.0278 0.21375C9.96447 0.14591 9.88926 0.0921149 9.80649 0.055439C9.72372 0.0187631 9.63502 -7.52589e-05 9.54545 2.25953e-07H1.36364C1.00198 2.25953e-07 0.655131 0.153983 0.3994 0.428075C0.143668 0.702166 0 1.07391 0 1.46154V17.5385C0 17.9261 0.143668 18.2978 0.3994 18.5719C0.655131 18.846 1.00198 19 1.36364 19H13.6364C13.998 19 14.3449 18.846 14.6006 18.5719C14.8563 18.2978 15 17.9261 15 17.5385V5.84615C15.0001 5.75016 14.9825 5.65509 14.9483 5.56638C14.9141 5.47767 14.8639 5.39705 14.8006 5.32913ZM10.2273 2.49466L12.6724 5.11539H10.2273V2.49466ZM13.6364 17.5385H1.36364V1.46154H8.86364V5.84615C8.86364 6.03997 8.93547 6.22584 9.06334 6.36289C9.1912 6.49993 9.36462 6.57692 9.54545 6.57692H13.6364V17.5385ZM10.9091 10.2308C10.9091 10.4246 10.8373 10.6105 10.7094 10.7475C10.5815 10.8845 10.4081 10.9615 10.2273 10.9615H4.77273C4.5919 10.9615 4.41847 10.8845 4.29061 10.7475C4.16274 10.6105 4.09091 10.4246 4.09091 10.2308C4.09091 10.037 4.16274 9.85108 4.29061 9.71404C4.41847 9.57699 4.5919 9.5 4.77273 9.5H10.2273C10.4081 9.5 10.5815 9.57699 10.7094 9.71404C10.8373 9.85108 10.9091 10.037 10.9091 10.2308ZM10.9091 13.1538C10.9091 13.3477 10.8373 13.5335 10.7094 13.6706C10.5815 13.8076 10.4081 13.8846 10.2273 13.8846H4.77273C4.5919 13.8846 4.41847 13.8076 4.29061 13.6706C4.16274 13.5335 4.09091 13.3477 4.09091 13.1538C4.09091 12.96 4.16274 12.7742 4.29061 12.6371C4.41847 12.5001 4.5919 12.4231 4.77273 12.4231H10.2273C10.4081 12.4231 10.5815 12.5001 10.7094 12.6371C10.8373 12.7742 10.9091 12.96 10.9091 13.1538Z"
                  fill="#0F0F74"
                />
              </svg>
              <span className="text-brand-900 font-semibold">
                {recording.contactName}
              </span>
            </div>
            <span className="text-brand-900 font-semibold">
              {recording.date}
            </span>
          </div>
          <div className="bg-clip-text text-gray overflow-y-auto max-h-[240px] [&::-webkit-scrollbar-thumb]:bg-secondary/80 [&::-webkit-scrollbar-track]:bg-secondary/20 [&::-webkit-scrollbar]:w-2">
            {clTranscriptParsed?.map((transcript, index) => (
              <p
                key={index}
                className={`${
                  transcript.channel === '1' ? 'text-blue-900' : 'text-gray-500'
                } mb-2 font-semibold`}
              >
                {transcript.channel === '1'
                  ? 'Caller: ' + transcript.text
                  : 'Prospect: ' + transcript.text}
              </p>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 py-4 px-5 border-t border-t-base-200">
          <Button
            title="Cancel"
            variant="blue"
            onClick={onClose}
            disabled={isTranscriptLoading}
          />
          <Button
            title="Download"
            iconStart={<DownloadIcon className="w-5 h-5 text-white" />}
            disabled={isTranscriptLoading}
            onClick={downloadTranscript}
          />
        </div>
      </div>
    </BasicModal>
  );
};
